/**
 * Ce que l'accueil apprenant dit à quelqu'un qui vient d'arriver (#26).
 *
 * L'écran listait des cartes sans un mot adressé à la personne : un inventaire,
 * là où l'apprenant convoqué à une formation obligatoire a d'abord besoin
 * d'être accueilli et de savoir par où reprendre. Les deux décisions de cet
 * accueil — quel bonjour, et quelle phrase de situation — sont isolées ici en
 * fonctions pures : elles ne dépendent que de la donnée déjà chargée par
 * `GQL_MY_SESSIONS`, et se testent sans monter un composant.
 */

import type { TFunction } from 'i18next'

const DAY_MS = 86_400_000

/**
 * Au-delà, une échéance n'est plus une information utile mais une source
 * d'angoisse gratuite : à trois mois de la fin, l'apprenant n'a rien à faire
 * de différent aujourd'hui.
 */
const DEADLINE_HORIZON_DAYS = 14

export type DayPeriod = 'morning' | 'afternoon' | 'evening'

/**
 * Moment de la journée, lu dans le fuseau du **navigateur** et non dans celui
 * de l'organisation.
 *
 * C'est l'exception à la règle posée par `session-dates.ts` : une échéance de
 * parcours appartient au calendrier de l'org, mais « bonsoir » doit
 * correspondre à ce que l'apprenant a par la fenêtre, pas aux horaires de
 * bureau de son employeur.
 */
export function dayPeriod(now: Date = new Date()): DayPeriod {
  const hour = now.getHours()
  if (hour < 5 || hour >= 18) return 'evening'
  if (hour < 12) return 'morning'
  return 'afternoon'
}

/** Vue minimale d'un parcours pour décider de la phrase de situation. */
export type FocusSession = {
  id: string
  name: string
  endDate?: unknown
  progress?: { completed?: boolean | null } | null
  /** L'attestation, évaluée par l'API (#10) : terminer ne suffit pas toujours à l'obtenir. */
  certificate?: { unlocked: boolean } | null
  modules?: readonly ({ myTracking?: { updatedAt?: unknown } | null } | null)[] | null
}

/**
 * La seule phrase que l'accueil met en avant. Volontairement **une** : empiler
 * « reprenez ceci », « il reste cela » et « attention à l'échéance » rendrait
 * l'ensemble illisible et ne dirait plus rien.
 */
export type LearnerFocus
  = | { kind: 'allDone' }
    | { kind: 'allDoneCertificatesPending' }
    | { kind: 'deadline', session: string, sessionId: string, daysLeft: number }
    | { kind: 'resume', session: string, sessionId: string }
    | { kind: 'start', session: string, sessionId: string }
    | null

function toTime(value: unknown): number | null {
  if (!value) return null
  const time = new Date(value as string).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Le parcours désigné, par son nom pour la phrase et par son id pour le bouton
 * qui y mène (#307).
 */
function named(session: FocusSession): { session: string, sessionId: string } {
  return { session: session.name, sessionId: session.id }
}

/** Dernière trace d'activité sur ce parcours, tous modules confondus. */
function lastTouched(session: FocusSession): number | null {
  let latest: number | null = null
  for (const step of session.modules ?? []) {
    const time = toTime(step?.myTracking?.updatedAt)
    if (time != null && (latest == null || time > latest)) latest = time
  }
  return latest
}

/**
 * Choisit la phrase de situation, par ordre d'utilité décroissante :
 *
 * 1. plus rien à faire — on le dit, plutôt que de laisser l'apprenant chercher ;
 * 2. une échéance proche — la seule information que l'écran ne montre nulle
 *    part ailleurs, et la seule qui se périme ;
 * 3. un parcours entamé — on ramène là où il s'est arrêté, le plus récent
 *    d'abord ;
 * 4. rien d'entamé — on désigne par quoi commencer.
 *
 * `null` quand l'apprenant n'a aucun parcours : l'écran a déjà son état vide,
 * qui dit mieux les choses qu'une phrase de plus.
 */
export function learnerFocus(sessions: readonly FocusSession[], now: Date = new Date()): LearnerFocus {
  if (sessions.length === 0) return null

  const pending = sessions.filter(s => s.progress?.completed !== true)
  if (pending.length === 0) {
    // Tout est fini, mais une attestation attend encore sa condition (#10) :
    // promettre « vos attestations sont téléchargeables » serait faux.
    return sessions.some(s => s.certificate?.unlocked === false)
      ? { kind: 'allDoneCertificatesPending' }
      : { kind: 'allDone' }
  }

  const nowTime = now.getTime()
  const deadlines = pending
    .flatMap((session) => {
      const end = toTime(session.endDate)
      if (end == null) return []
      // `endDate` sort de l'API à la fin du jour local de l'org : un plancher
      // sur le nombre de jours pleins restants donne bien 0 le dernier jour.
      const daysLeft = Math.floor((end - nowTime) / DAY_MS)
      // Une échéance dépassée ne se rappelle pas ici. L'apprenant n'y peut plus
      // rien, et l'accueil n'est pas l'endroit où on le lui reproche.
      if (daysLeft < 0 || daysLeft > DEADLINE_HORIZON_DAYS) return []
      return [{ session, daysLeft }]
    })
    .sort((a, b) => a.daysLeft - b.daysLeft)

  const closest = deadlines[0]
  if (closest) return { kind: 'deadline', ...named(closest.session), daysLeft: closest.daysLeft }

  const started = pending
    .flatMap((session) => {
      const touched = lastTouched(session)
      return touched == null ? [] : [{ session, touched }]
    })
    .sort((a, b) => b.touched - a.touched)

  const latest = started[0]
  if (latest) return { kind: 'resume', ...named(latest.session) }

  return { kind: 'start', ...named(pending[0]!) }
}

/**
 * Met en mots la situation choisie par `learnerFocus`.
 *
 * Le dernier jour a sa propre clé : le pluriel français range 0 avec le
 * singulier, si bien qu'un `count: 0` afficherait « il vous reste 0 jour ».
 */
export function focusLine(focus: LearnerFocus, t: TFunction): string | null {
  switch (focus?.kind) {
    case 'allDone':
      return t('home.focus.allDone')
    case 'allDoneCertificatesPending':
      return t('home.focus.allDoneCertificatesPending')
    case 'deadline':
      return focus.daysLeft === 0
        ? t('home.focus.deadlineToday', { session: focus.session })
        : t('home.focus.deadline', { count: focus.daysLeft, session: focus.session })
    case 'resume':
      return t('home.focus.resume', { session: focus.session })
    case 'start':
      return t('home.focus.start', { session: focus.session })
    default:
      return null
  }
}
